<template>
  <slot name="link" :clicked="toggleModal" :open="openModal" :close="closeModal" />

  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modal-wrapper"
      @click.self="onBackdropClick">
      <div
        ref="modalElement"
        class="modal"
        :class="{ 'modal--wide': modalVariant === ModalVariant.Wide, 'modal--bottom': bottom }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="heading ? headingId : undefined"
        :aria-describedby="content ? contentId : undefined"
        tabindex="-1">
        <button
          ref="closeButtonElement"
          class="modal__close-button"
          type="button"
          :aria-label="translations['bookings.common.close']"
          @click="closeModal">
          <span class="icon icon--cross" aria-hidden="true" />
        </button>

        <div v-if="heading || content" class="modal__information">
          <h1 v-if="heading" :id="headingId">{{ heading }}</h1>
          <p v-if="content" :id="contentId" class="modal__preamble" v-html="content" />
        </div>

        <slot name="content" :close="closeModal" />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ModalVariant } from "./modal-variants";
import {getTranslations} from "@services/translations";

const translations = getTranslations();

const props = defineProps<{  
  show?: boolean;
  bottom?: boolean;
  heading?: string;
  content?: string;
  closeOnBackdrop?: boolean;
  variant?: ModalVariant;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "closeModal"): void;
}>();

const isOpen = ref(props.show === true);
const modalVariant = computed(() => props.variant ?? ModalVariant.Default);
const modalElement = ref<HTMLElement | null>(null);
const closeButtonElement = ref<HTMLButtonElement | null>(null);
const lastFocusedElement = ref<HTMLElement | null>(null);
const modalId = `modal-${getCurrentInstance()?.uid ?? 0}`;
const headingId = `${modalId}-heading`;
const contentId = `${modalId}-content`;

watch(
  () => props.show,
  (newValue) => {
    const shouldOpen = newValue === true;
    if (shouldOpen !== isOpen.value) {
      setOpen(shouldOpen);
    }
  },
);

watch(isOpen, (newValue) => {
  emit("update:show", newValue);
  toggleBodyClass(newValue);

  if (newValue) {
    window.addEventListener("resize", updateScrollableState);
    nextTick(() => {
      updateScrollableState();
      focusModal();
    });
    return;
  }

  window.removeEventListener("resize", updateScrollableState);
  restoreFocus();
});

onMounted(() => {
  window.addEventListener("keydown", onKeyDown);
  if (isOpen.value) {
    toggleBodyClass(true);
    nextTick(() => {
      updateScrollableState();
      focusModal();
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onKeyDown);
  window.removeEventListener("resize", updateScrollableState);
  toggleBodyClass(false);
});

function setOpen(value: boolean) {
  if (value && !isOpen.value) {
    lastFocusedElement.value = document.activeElement as HTMLElement | null;
  }

  isOpen.value = value;
}

function openModal() {
  setOpen(true);
}

function closeModal() {
  setOpen(false);
  emit("closeModal");
}

function toggleModal() {
  setOpen(!isOpen.value);
}

function onBackdropClick() {
  if (props.closeOnBackdrop !== false) {
    closeModal();
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key === "Escape" && isOpen.value) {
    closeModal();
  }
}

function updateScrollableState() {
  if (!modalElement.value) {
    return;
  }

  const modal = modalElement.value;
  const shouldScroll = modal.scrollHeight > window.innerHeight;
  modal.classList.toggle("modal--scroll", shouldScroll);
}

function focusModal() {
  const focusTarget = closeButtonElement.value ?? modalElement.value;
  focusTarget?.focus();
}

function restoreFocus() {
  lastFocusedElement.value?.focus();
  lastFocusedElement.value = null;
}

function toggleBodyClass(forceState: boolean) {
  const body = document.body;
  if (forceState) {
    body.classList.add("modal-open");
    return;
  }

  nextTick(() => {
    const hasOpenModals = document.querySelectorAll(".modal-wrapper").length > 0;
    body.classList.toggle("modal-open", hasOpenModals);
  });
}
</script>
